import React from 'react';
import { Table, Button } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

// 학생 관리 페이지용 인터페이스
interface StudentData {
  key: string;
  no: number;
  academyCode: string;
  studentName: string;
  studentId: string;
  academyStudentId: string;
  birthDate: string;
  status: 'active' | 'inactive' | 'withdrawn';
  startDate: string;
}

// 학습 상세 페이지용 인터페이스
interface LearningStudent {
  id: string;
  name: string;
  academy: string;
  grade: string;
  status: 'active' | 'inactive';
}

interface StudentTableProps {
  students: StudentData[] | LearningStudent[];
  loading?: boolean;
  pagination?: false | TablePaginationConfig;
  size?: 'small' | 'middle' | 'large';
  rowSelection?: any;
  onRow?: (record: any) => any;
  onRemoveStudent?: (student: any) => void;
  type?: 'management' | 'learning';
  showRemoveButton?: boolean;
}

const StudentTable: React.FC<StudentTableProps> = ({ 
  students, 
  loading = false, 
  pagination = false,
  size = 'middle',
  rowSelection,
  onRow,
  onRemoveStudent,
  type = 'management',
  showRemoveButton = false
}) => {
  const getColumns = (): ColumnsType<any> => {
    if (type === 'learning') {
      const columns: any[] = [
        { 
          title: '순번', 
          dataIndex: 'no', 
          key: 'no', 
          width: 80, 
          align: 'center' as const
        },
        { 
          title: '학원코드', 
          dataIndex: 'academyCode', 
          key: 'academyCode', 
          width: 120 
        },
        { 
          title: '학생명', 
          dataIndex: 'studentName', 
          key: 'studentName', 
          width: 120 
        },
        { 
          title: '생년월일', 
          dataIndex: 'birthDate', 
          key: 'birthDate', 
          width: 120 
        },
      ];

      // 삭제 버튼이 필요한 경우 추가
      if (showRemoveButton) {
        columns.push({
          title: '삭제',
          key: 'action',
          width: 100,
          render: (text: string, record: any) => (
            <Button 
              type="primary" 
              danger
              size="small"
              onClick={() => onRemoveStudent?.(record)}
            >
              삭제
            </Button>
          ),
        });
      }

      return columns;
    }

    // management type (기본값)
    return [
    { 
      title: '순번', 
      dataIndex: 'no', 
      key: 'no', 
      width: 80, 
      align: 'center' as const
    },
    { 
      title: '학원코드', 
      dataIndex: 'academyCode', 
      key: 'academyCode', 
      width: 120 
    },
    { 
      title: '학생명', 
      dataIndex: 'studentName', 
      key: 'studentName', 
      width: 120 
    },
    { 
      title: '학생ID', 
      dataIndex: 'studentId', 
      key: 'studentId', 
      width: 120 
    },
    { 
      title: '생년월일', 
      dataIndex: 'birthDate', 
      key: 'birthDate', 
      width: 120 
    },
    { 
      title: '학생상태', 
      dataIndex: 'status', 
      key: 'status', 
      width: 120,
      render: (status: string) => {
        const statusConfig = {
          active: { text: '정상', color: 'green' },
          inactive: { text: '휴면', color: 'orange' },
          withdrawn: { text: '퇴원', color: 'red' }
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return (
          <span style={{ 
            color: config.color, 
            fontWeight: 'bold' 
          }}>
            {config.text}
          </span>
        );
      }
    },
    { 
      title: '수강시작일', 
      dataIndex: 'startDate', 
      key: 'startDate', 
      width: 120 
      },
    ];
  };

  const studentColumns = getColumns();

  return (
    <Table
      columns={studentColumns}
      dataSource={students}
      rowKey={type === 'learning' ? 'id' : 'academyStudentId'}
      pagination={pagination}
      loading={loading}
      size={size}
      rowSelection={rowSelection}
      onRow={onRow}
      scroll={{ x: 600 }}
    />
  );
};

export default StudentTable; 